import { Button, Table, Tag, Popover, message } from "antd";
import { useNavigate } from "react-router";
import type { ColumnsType } from "antd/es/table";
import { UseMutateAsyncFunction, useMutation } from "react-query";
import { deleteWarehouses, getWarehousesById } from "../../api/warehouse";
import { useTranslation } from "react-i18next";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import DeleteModal from "../UI/ModalComponent/DeleteModal/deletemodal";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ShopOutlined } from "@ant-design/icons";
import { AxiosResponse } from "axios";
interface WarehouseListProps {
  WarehouseMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean;
    },
    unknown
  >;
  isWarehouseLoading: boolean;
  search: string;
  paginatedWarehouse: any;
  setPaginatedWarehouse: Dispatch<any>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  filter: boolean | undefined;
  tableKey: string;
}
interface WarehouseInterface {
  id: string;
  name: string;
  guid: string;
  description: string;
  location: string;
  isActive: boolean;
}
export const WarehouseList = ({
  WarehouseMutation,
  isWarehouseLoading,
  search,
  paginatedWarehouse,
  setPaginatedWarehouse,
  page,
  setPage,
  filter,
  tableKey,
}: WarehouseListProps) => {
  const [t] = useTranslation("common");
  const [, setError] = useState("");
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const navigate = useNavigate();
  const content = <div>{t("stock")}</div>;
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      WarehouseMutation({
        limit: page * 10 + 10,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedWarehouse(res.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const deleteWarehousesMutation = useMutation(
    "deleteWarehouses",
    deleteWarehouses,
    {
      onSuccess: () => {
        WarehouseMutation({
          limit: page * 10 + 10,
          q: search,
          isActive: filter,
        }).then((res: any) => {
          setPaginatedWarehouse(res.data);
          message.success("Complete successfully");
        });
      },
      onError,
    }
  );

  const handleDeleteWarehouse = (
    id: string | undefined,
    name: string
  ): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteWarehousesMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const getWarehousesMutation = useMutation(
    " getWarehouses",
    getWarehousesById
  );

  function fillWarehouseById(id: any) {
    getWarehousesMutation.mutateAsync(id).then((res) => {
      navigate(`/warehouses/${id}`, {
        state: {
          warehouse: res.data,
        },
      });
    });
  }

  const columns: ColumnsType<WarehouseInterface> = [
    {
      title: `${t("name")}`,
      dataIndex: "name",
      key: "name",
      width: "25rem",
      className: "table-header",
    },
    {
      title: `${t("location")}`,
      dataIndex: "location",
      key: "location",
    },
    {
      title: `${t("description")}`,
      dataIndex: "description",
      key: "description",
    },
    {
      title: `${t("status")}`,
      align: "center",
      dataIndex: "isActive",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      align: "center",
      key: "action",
      width: "8rem",
      fixed: "right",
      render: (record: any) => (
        <>
          <Popover content={content} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              onClick={() => {
                navigate(`/stock/${record?.id}`);
              }}
              type="text"
              icon={
                <ShopOutlined style={{ fontSize: "16px", color: "#212121" }} />
              }
            />
          </Popover>
          <EditButton onClick={() => fillWarehouseById(record.id)} />
          <DeleteButton
            onClick={() => handleDeleteWarehouse(record.id, record.name)}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        dataSource={paginatedWarehouse}
        loading={isWarehouseLoading}
        columns={columns}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record: any) => record.id}
      />
    </>
  );
};
